"use client";

/**
 * #434 — diálogo de confirmação do botão "Reprocessar levantamento".
 *
 * Decisão ratificada (Lucas, ADR-VID-005 adendo 2026-07-03): re-processar um
 * levantamento JÁ VALIDADO cria um CLONE (nova versão) apontando pros mesmos
 * vídeos; a versão atual continua vigente e intocada até a nova ser
 * validada. O diálogo nomeia esse efeito explicitamente antes de confirmar
 * (nunca um clique único sem aviso).
 *
 * Reusa os primitivos `Dialog` existentes (mesmo padrão de
 * `EditLevantamentoDialog`) -- este projeto não tem um componente shadcn
 * "AlertDialog" próprio; o `Dialog` (@base-ui/react/dialog) já cobre
 * confirmação com um footer de 2 botões.
 */

import { useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";

import { useReprocessarLevantamento } from "@/lib/api-video";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ReprocessarLevantamentoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  levantamentoId: string;
}

export function ReprocessarLevantamentoDialog({
  open,
  onOpenChange,
  levantamentoId,
}: ReprocessarLevantamentoDialogProps) {
  const router = useRouter();
  const reprocessarMut = useReprocessarLevantamento(levantamentoId);
  // Follow-up #434 (mesmo branch/PR) — reflete o default do backend (true):
  // caso típico é mesma câmera/enquadramento, clonar poupa redesenhar.
  const [clonarLinhas, setClonarLinhas] = useState(true);

  function handleConfirm() {
    reprocessarMut.mutate(
      { clonar_linhas: clonarLinhas },
      {
        onSuccess: (clone) => {
          onOpenChange(false);
          toast.success(
            "Nova versão criada — a atual continua vigente até a nova ser validada.",
          );
          router.push(`/${clone.id}`);
        },
        onError: (err) => {
          toast.error(
            (err as Error)?.message ?? "Falha ao reprocessar levantamento.",
          );
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        showCloseButton={false}
        data-testid="reprocessar-dialog"
      >
        <DialogHeader>
          <DialogTitle>Reprocessar levantamento validado</DialogTitle>
          <DialogDescription>
            Este levantamento já foi validado — re-processar cria uma{" "}
            <strong>nova versão</strong> (apontando pros mesmos vídeos). A
            versão atual continua <strong>vigente</strong> e intocada até a
            nova versão ser validada.
          </DialogDescription>
        </DialogHeader>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="rounded"
            checked={clonarLinhas}
            onChange={(e) => setClonarLinhas(e.target.checked)}
            data-testid="checkbox-clonar-linhas"
          />
          Clonar linhas de corte da versão atual
        </label>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={reprocessarMut.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={reprocessarMut.isPending}
            data-testid="btn-confirm-reprocessar"
          >
            {reprocessarMut.isPending
              ? "Criando nova versão…"
              : "Confirmar e reprocessar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
